import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import { Button, CircularProgress, Typography, Paper, Box } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BusinessIcon from '@mui/icons-material/Business';
import type { DivisionMembership } from 'src/store/modules/auth/v2Operations';

const Container = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '40px 20px',
  maxWidth: '500px',
  margin: '0 auto'
});

const Title = styled(Typography)({
  marginBottom: '8px',
  fontWeight: 600,
  color: '#1a1a2e'
});

const Subtitle = styled(Typography)({
  marginBottom: '32px',
  color: '#666'
});

const DivisionList = styled('div')({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  marginBottom: '24px'
});

const DivisionCard = styled(Paper)<{ selected?: boolean }>(({ selected }) => ({
  padding: '16px 20px',
  cursor: 'pointer',
  border: selected ? '2px solid #1976d2' : '2px solid transparent',
  backgroundColor: selected ? '#f5f9ff' : '#fff',
  transition: 'all 0.2s ease',
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  '&:hover': {
    backgroundColor: selected ? '#f5f9ff' : '#f5f5f5',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
  }
}));

const DivisionIcon = styled('div')<{ selected?: boolean }>(({ selected }) => ({
  width: '48px',
  height: '48px',
  borderRadius: '8px',
  backgroundColor: selected ? '#1976d2' : '#e0e0e0',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: selected ? '#fff' : '#666'
}));

const DivisionInfo = styled('div')({
  flex: 1
});

const DivisionName = styled(Typography)({
  fontWeight: 600,
  fontSize: '16px',
  color: '#1a1a2e'
});

const CompanyName = styled(Typography)({
  fontSize: '14px',
  color: '#666'
});

const RolesContainer = styled('div')({
  display: 'flex',
  gap: '8px',
  marginTop: '8px',
  flexWrap: 'wrap'
});

const RoleBadge = styled('span')<{ role: string }>(({ role }) => {
  const colors: Record<string, { bg: string; text: string }> = {
    admin: { bg: '#e8f5e9', text: '#2e7d32' },
    'read-only': { bg: '#fff3e0', text: '#f57c00' },
    user: { bg: '#e3f2fd', text: '#1976d2' }
  };
  const { bg, text } = colors[role?.toLowerCase()] || colors.user;
  return {
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: 500,
    backgroundColor: bg,
    color: text,
    textTransform: 'capitalize'
  };
});

const ContinueButton = styled(Button)({
  width: '100%',
  padding: '12px 24px',
  fontSize: '16px',
  textTransform: 'none',
  fontWeight: 600
});

const UserInfo = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '24px',
  padding: '12px 16px',
  backgroundColor: '#f5f5f5',
  borderRadius: '8px',
  width: '100%'
});

interface DivisionSelectorProps {
  userName: string;
  userEmail: string;
  divisions: DivisionMembership[];
  lastActiveDivisionId?: string;
  onSelectDivision: (divisionId: string) => void;
  loading?: boolean;
  appName?: string;
}

const DivisionSelector: React.FC<DivisionSelectorProps> = ({
  userName,
  userEmail,
  divisions,
  lastActiveDivisionId,
  onSelectDivision,
  loading = false,
  appName = 'atlas'
}) => {
  const [selectedDivisionId, setSelectedDivisionId] = useState<string>(
    lastActiveDivisionId || divisions[0]?.id || ''
  );

  const handleDivisionClick = (divisionId: string) => {
    setSelectedDivisionId(divisionId);
  };

  const handleContinue = () => {
    if (selectedDivisionId) {
      onSelectDivision(selectedDivisionId);
    }
  };

  return (
    <Container>
      <CheckCircleIcon sx={{ fontSize: 48, color: '#4caf50', marginBottom: '16px' }} />
      
      <Title variant="h5">Welcome back!</Title>
      <Subtitle variant="body1">Select a division to continue</Subtitle>

      <UserInfo>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body1" fontWeight={600}>
            {userName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {userEmail}
          </Typography>
        </Box>
      </UserInfo>

      <DivisionList>
        {divisions.map((division) => {
          const isSelected = selectedDivisionId === division.id;
          const appRole = division.appAccess?.[appName?.toLowerCase()] || division.appAccess?.atlas;
          
          return (
            <DivisionCard
              key={division.id}
              selected={isSelected}
              elevation={isSelected ? 2 : 1}
              onClick={() => handleDivisionClick(division.id)}
            >
              <DivisionIcon selected={isSelected}>
                <BusinessIcon />
              </DivisionIcon>
              <DivisionInfo>
                <DivisionName>{division.companyName || division.name || division.id}</DivisionName>
                {division.companyName && (
                  <span style={{ fontSize: 12, fontWeight: 500, color: '#7E8FA8', display: 'block' }}>{division.name}</span>
                )}
                <RolesContainer>
                  {division.appAccess && Object.entries(division.appAccess)
                    .filter(([, level]) => level !== 'blocked')
                    .map(([app, level]) => (
                    <RoleBadge key={app} role={level}>
                      {app}: {level}
                    </RoleBadge>
                  ))}
                </RolesContainer>
              </DivisionInfo>
              {isSelected && (
                <CheckCircleIcon sx={{ color: '#1976d2' }} />
              )}
            </DivisionCard>
          );
        })}
      </DivisionList>

      <ContinueButton
        variant="contained"
        color="primary"
        onClick={handleContinue}
        disabled={!selectedDivisionId || loading}
      >
        {loading ? (
          <CircularProgress size={24} color="inherit" />
        ) : (
          'Continue'
        )}
      </ContinueButton>
    </Container>
  );
};

export default DivisionSelector;
